import { memo, useCallback } from "react";
import {
    GRID_ACTIONS_COLUMN_TYPE,
    GridActionsCellItem,
    type GridRowParams,
    type GridRowIdGetter
} from "@mui/x-data-grid";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Outlet } from "infrastructure/router";
import { TableGridMode } from "consts/table";
import { WebRoutes } from "consts/enpoints/web";
import { Filters } from "consts/onboarding";
import { when } from "util/support";
import type { Application } from "features/onboarding/types";
import {
    selectIsApplicationsUninitialized,
    selectIsApplicationsLoading,
    selectApplicationsSlice
} from "features/onboarding/selectors";
import useActions from "features/onboarding/useActions";
import { disableCta } from "features/onboarding/helpers";
import Resource from 'ui/layouts/Resource';
import Table, { TableProps } from "ui/widgets/Table";
import { Toolbar } from './components';
import { ApplicationStatus } from './columns';
import {
    useApplicationTableResourceEvents,
    useApplicationTableResourceFilters
} from "./hooks";
import {
    AddMerchantManually,
    ApproveManuallyApplication,
    RejectApplication,
    MarkIdleApplication,
    DeleteApplication,
    DownloadApplication,
} from "./actions";
import ApplicationTableResourceProvider, {
    Action,
    useApplicationTableResourceContext
} from "./ApplicationTableResourceProvider";

type Props = Omit<
    TableProps,
    | 'mode'
    | 'selectIsTableUninitialized'
    | 'selectIsTableLoading'
    | 'selectTableDataSlice'
    | 'filterTypes'
> & {
    readonly resourceName: string;
};

const ApplicationsTableResource = ({ resourceName, ...restTableProps }: Props) => {
    const { resetApplications } = useActions();
    const { onRowClick } = useApplicationTableResourceEvents();
    const { filterTypes, filtersModelOverrides } = useApplicationTableResourceFilters();
    const { onOpen } = useApplicationTableResourceContext();

    const getRowId: GridRowIdGetter<Application> = useCallback(({ pki }) => pki, []);

    return (
        <>
            <Resource
                sx={{
                    position: 'relative'
                }}
                breadcrumbsProps={{
                    heading: 'Onboarding',
                    action: (
                        <AddMerchantManually />
                    ),
                    links: [
                        {
                            name: 'Applications',
                            href: WebRoutes.Onboarding.Applications.Index
                        },
                        {
                            name: resourceName
                        }
                    ],
                    sx: {
                        mb: 0
                    }
                }}
            >
                <Table
                    {...restTableProps}
                    initialState={{
                        pinnedColumns: {
                            right: [GRID_ACTIONS_COLUMN_TYPE]
                        }
                    }}
                    filterTypes={filterTypes}
                    mode={TableGridMode.OnboardingApplications}
                    selectIsTableUninitialized={selectIsApplicationsUninitialized}
                    selectIsTableLoading={selectIsApplicationsLoading}
                    selectTableDataSlice={selectApplicationsSlice}
                    resetState={resetApplications}
                    components={{
                        Toolbar
                    }}
                    onRowClick={onRowClick}
                    getRowId={getRowId}
                    columns={[
                        {
                            field: Filters.status,
                            headerName: 'Status',
                            minWidth: 170,
                            renderCell: ApplicationStatus
                        },
                        {
                            field: GRID_ACTIONS_COLUMN_TYPE,
                            type: GRID_ACTIONS_COLUMN_TYPE,
                            getActions: (rowParams: GridRowParams) => when(
                                disableCta(rowParams.row.status),
                                [],
                                [
                                    <DownloadApplication
                                        {...rowParams}
                                    />,
                                    <GridActionsCellItem
                                        showInMenu
                                        icon={(
                                            <CheckCircleIcon
                                                color='success'
                                            />
                                        )}
                                        label={`Approve manually application: ${rowParams.id}`}
                                        onClick={() => onOpen({
                                            action: Action.ApproveApplication,
                                            row: rowParams.row
                                        })}
                                    />,
                                    <GridActionsCellItem
                                        showInMenu
                                        icon={(
                                            <ThumbDownIcon
                                                color='warning'
                                            />
                                        )}
                                        label={`Reject application: ${rowParams.id}`}
                                        onClick={() => onOpen({
                                            action: Action.RejectApplication,
                                            row: rowParams.row
                                        })}
                                    />,
                                    <GridActionsCellItem
                                        showInMenu
                                        icon={(
                                            <AcUnitIcon
                                                color='info'
                                            />
                                        )}
                                        label={`Mark as "IDLE" application: ${rowParams.id}`}
                                        onClick={() => onOpen({
                                            action: Action.IdleApplication,
                                            row: rowParams.row
                                        })}
                                    />,
                                    <GridActionsCellItem
                                        showInMenu
                                        icon={(
                                            <DeleteForeverIcon
                                                color='error'
                                            />
                                        )}
                                        label={`Delete application: ${rowParams.id}`}
                                        onClick={() => onOpen({
                                            action: Action.DeleteApplication,
                                            row: rowParams.row
                                        })}
                                    />
                                ]
                            )
                        }
                    ]}
                    pinnedColumnsSx={{
                        right: {
                            width: 90
                        }
                    }}
                    LayoutProps={{
                        sx: {
                            m: '24px 0px'
                        }
                    }}
                    FiltersProps={{
                        sx: {
                            m: 0
                        },
                        filtersModelOverrides
                    }}
                />
                <Outlet />
            </Resource>
            <ApproveManuallyApplication />
            <RejectApplication />
            <MarkIdleApplication />
            <DeleteApplication />
        </>
    );
};

export default memo((props: Props) => (
    <ApplicationTableResourceProvider>
        <ApplicationsTableResource
            {...props}
        />
    </ApplicationTableResourceProvider>
));
